import type { NetworkErrorCode } from "../network/types.ts";

export type Operation = "search" | "detail" | "chapters" | "pages";
export type Value = string | number | boolean | null | Value[] | { [key: string]: Value };
export type RecordValue = Record<string, Value>;
export type Transform = "trim" | "absolute-url" | "number" | "srcset";
export interface DecoderRule {
  kind: "builtin";
  name: string;
}
export interface FieldRule {
  asset?: boolean;
  all?: boolean;
  label?: string;
  valueSelector?: string;
  selector?: string;
  path?: string;
  attributes?: string[];
  transforms?: Transform[];
  decoder?: DecoderRule;
  required?: boolean;
}
export interface ExtractionRule {
  kind: "html" | "json";
  selector?: string;
  path?: string;
  fields: Record<string, FieldRule>;
  // Empty lists require a positive empty-state match, not just a missing card selector.
  emptySelector?: string;
  allowEmpty?: boolean;
  decoder?: DecoderRule;
}
export interface SourceStep {
  url: string;
  method?: "GET" | "POST";
  referer?: string;
  headers?: Record<string, string>;
  form?: Record<string, string>;
  capture?: Record<string, FieldRule>;
  extract?: ExtractionRule;
}
export interface SourceProfile {
  schemaVersion: 1;
  id: string;
  name: string;
  baseUrl: string;
  allowedHosts: string[];
  template: "Madara" | "MangaThemesia" | "TMO" | "LeerCapitulo" | "CustomCSS";
  corsOpen?: boolean;
  requiresSession?: boolean;
  cacheTtlMs?: number;
  operations: Record<Operation, OperationDefinition>;
  catalog?: OperationDefinition;
  latest?: OperationDefinition;
  ui?: {
    chapterDelivery?: 'external-file';
    pageSize?: number;
    latestOrder?: "added" | "updated";
    iconUrl?: string;
    mangaPath: string;
    chapterPath: string;
    idMode: "path" | "url";
    languages?: string[];
    trailingSlash?: boolean;
    coverPath?: string;
    paginationParams?: Record<string, string>;
  };
}
export interface OperationDefinition {
  steps: SourceStep[];
  fallbacks?: { steps: SourceStep[] }[];
}
export interface OperationContext {
  query?: string;
  page?: number;
  manga?: { id: string; url: string };
  chapter?: { id: string; url: string };
  signal?: AbortSignal;
}
export type ErrorCode =
  | NetworkErrorCode
  | "INVALID_PROFILE"
  | "INVALID_URL"
  | "MISSING_VARIABLE"
  | "SELECTOR_MISMATCH"
  | "INVALID_JSON"
  | "DECODER_ERROR";
export class ExtractionError extends Error {
  constructor(
    public readonly code: ErrorCode,
    message: string,
  ) {
    super(message);
    this.name = "ExtractionError";
  }
}
export interface SourceDiagnostic {
  operation: Operation;
  stepIndex: number;
  url: string;
  status?: number;
  code: ErrorCode;
  message: string;
}
export class SourceEngineError extends Error {
  constructor(
    public readonly diagnostic: SourceDiagnostic,
    public readonly cause?: unknown,
  ) {
    super(
      `${diagnostic.operation} · paso ${diagnostic.stepIndex + 1} · ${diagnostic.code}: ${diagnostic.message}`,
    );
    this.name = "SourceEngineError";
  }
}
export interface OperationResult {
  nextUrl?: string;
  items: RecordValue[];
  finalUrl: string;
}
